import { MediaSequenceResponse } from 'api/core';
import { BRANCH_TEMPLATE_FORMATS } from './BranchTemplates';
import { useMemo } from 'react';
import {
  useCreateMediaSequenceBranch,
  useDeleteMediaSequenceBranch,
} from 'api/useMediaSequenceBranchesApi';
import { useGetMediaSequenceAssets } from 'api/useMediaSequencesApi';
import { twMerge } from 'tailwind-merge';
import { Link } from 'react-router';

interface TableProps {
  data?: MediaSequenceResponse;
  isFetching: boolean;
  disabled?: boolean;
}

interface BranchRow {
  height: number;
  width: number;
  name: string;
  recommendedUses: string[];
  isChecked: boolean;
  isBestFormat: boolean;
}

export const MediaSequenceBranchSelectionTable = ({
  data,
  disabled,
}: TableProps) => {
  const { mutateAsync: createAsync, isPending: isPendingCreate } =
    useCreateMediaSequenceBranch(false);
  const { mutateAsync: deleteAsync, isPending: isPendingDelete } =
    useDeleteMediaSequenceBranch(false);

  const { data: allAssets = [] } = useGetMediaSequenceAssets(data?.id ?? '');

  const bestFormats = useMemo(() => {
    const assetDimensions = allAssets
      .filter((msAsset) => msAsset.asset.height && msAsset.asset.width)
      .map((asset) => ({
        height: asset.asset.height as number,
        width: asset.asset.width as number,
      }));

    return BRANCH_TEMPLATE_FORMATS.filter((format) => {
      const hasMatchingDimensions = assetDimensions.some(
        (dimension) =>
          dimension.height === format.height && dimension.width === format.width
      );

      const hasMatchingAspectRatio = assetDimensions.some((dimension) => {
        const assetAspectRatio = dimension.width / dimension.height;
        const formatAspectRatio = format.width / format.height;
        return Math.abs(assetAspectRatio - formatAspectRatio) < 0.01;
      });

      return hasMatchingDimensions || hasMatchingAspectRatio;
    });
  }, [allAssets]);

  const handleToggleAsync = async (row: BranchRow) => {
    row.isChecked = !row.isChecked;
    if (row.isChecked) {
      await createAsync({
        mediaSequenceId: data?.id ?? '',
        mediaSequenceBranchCreateRequest: {
          height: row.height,
          width: row.width,
          name: row.name,
        },
      });
    } else {
      const id = data?.branches.find(
        (e) => e.height === row.height && e.width === row.width
      )?.id;
      if (id) await deleteAsync({ id });
    }
  };

  const rows = useMemo(() => {
    return BRANCH_TEMPLATE_FORMATS.map((format) => ({
      height: format.height,
      width: format.width,
      name: format.name,
      recommendedUses: format.recommendedUses,
      isChecked: !!data?.branches.some(
        (branch) =>
          branch.height === format.height && branch.width === format.width
      ),
      isBestFormat: bestFormats.some(
        (best) => best.height === format.height && best.width === format.width
      ),
    }));
  }, [data, bestFormats]);

  return (
    <div className="overflow-x-auto">
      {bestFormats.length > 0 ? (
        <div className="flex flex-col gap-3 p-4 bg-green-50 text-green-800 rounded-md mb-4">
          <p>
            Formater markeret med grøn er anbefalinger baseret på de filer du
            har uploadet til din video. Disse formater passer bedst i forhold
            til opløsning og billedformat og sikrer optimal visning. Det er blot
            en anbefaling, og du kan vælge andre formater, hvis det er
            nødvendigt.
          </p>
          <p>
            Du kan læse mere on de forskellige formater og deres anbefalede brug{' '}
            <Link
              to="/media-sequences/recording-guide/recording"
              className="link link-primary"
              target="_blank"
            >
              her
            </Link>
            .
          </p>
        </div>
      ) : null}
      <table className="table table-zebra">
        <thead>
          <tr>
            <th>Valgt</th>
            <th>Format</th>
            <th>Anbefalet brug</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={index}
              className={twMerge(
                row.isBestFormat && 'border-2 border-green-500'
              )}
            >
              <td>
                <input
                  type="checkbox"
                  checked={row.isChecked}
                  disabled={disabled || isPendingCreate || isPendingDelete}
                  onChange={async () => await handleToggleAsync(row)}
                  className="toggle toggle-primary"
                />
              </td>
              <td>{row.name}</td>
              <td>{row.recommendedUses.join(', ')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
